import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { ORDER_DUE_DATE_TYPE, ORDER_FULFILLMENT_TYPE, ORDER_REPORT_CSV_TYPE, ORDER_STATUS_TYPE } from '@/constants/enum';
import { formatDate } from '@/helpers/daterange';
import {
  useExportOrderReportCSVMutation,
  useExportProductSummaryMutation,
  useGetGeneralSettingQuery,
  useGetOrderLocationsQuery,
  useGetOrderReportSummaryMutation,
  useUpdateStatusOrderMutation,
} from '@/redux/query';
import {
  handleSaveOrderFilterTableSettings,
  handleSaveOrderPaginationSettings,
  handleSaveOrderTableSettings,
  orderFilterSettingSelector,
  orderPaginationSettingSelector,
  orderTableSettingSelector,
} from '@/redux/slice/order.slice';
import { IOrderLocation } from '@/types/components/orderReport';
import {
  BlockStack,
  Box,
  Card,
  ChoiceList,
  EmptySearchResult,
  IndexFilters,
  IndexFiltersMode,
  IndexFiltersProps,
  IndexTable,
  InlineStack,
  Pagination,
  useBreakpoints,
  useIndexResourceState,
  useSetIndexFiltersMode,
} from '@shopify/polaris';
import { Range } from '@shopify/polaris/build/ts/src/utilities/index-provider';
import { SelectionType } from '@shopify/polaris/build/ts/src/utilities/use-index-resource-state';
import { debounce, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CenterHeading, CustomPaddingBox, IndexFilterStyled } from '../styled';
import { HeadingInformation } from './component/CustomHeading/HeadingInfomation';
import { CalendarModal } from './component/Modal/CalendarModal';
import { EditColumnModal } from './component/Modal/EditColumnModal';
import RowData from './component/RowData';
import { DEFAULT_ORDER_COLUMN } from '@/constants';
import { NonEmptyArray } from '@shopify/polaris/build/ts/src/types';
import { IndexTableHeading } from '@shopify/polaris/build/ts/src/components/IndexTable';

const OrderTable = () => {
  const { t } = useTranslation('orderReport');

  const resourceName = {
    singular: 'order',
    plural: 'order',
  };

  const today = useMemo(() => new Date(), []);
  const { mode, setMode } = useSetIndexFiltersMode();
  const [search, setSearch] = useState({
    queryValue: '',
    debouncedSearchValue: '',
  });
  const dispatch = useDispatch();
  const orderFilterSettings = useSelector(orderFilterSettingSelector);
  const orderTableSettings = useSelector(orderTableSettingSelector);
  const orderPaginationSettings = useSelector(orderPaginationSettingSelector);
  const [sortSelected, setSortSelected] = useState(['orderNumber desc']);
  const [showModal, setShowModal] = useState({
    isShowCalendarModal: false,
    isShowEditColumnModal: false,
  });
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });
  const isFilterApplied = useRef(false);
  const [isCallbackApi, setIsCallbackApi] = useState(false);
  const [getReportSummary, { isLoading: isLoadingGetOrderReport }] = useGetOrderReportSummaryMutation();
  const { data: orderLocationData } = useGetOrderLocationsQuery({ shop: config.shop });
  const [exportOrderReportCSV] = useExportOrderReportCSVMutation();
  const [exportProductSummary] = useExportProductSummaryMutation();
  const [updateOrderStatus] = useUpdateStatusOrderMutation();
  const { data: generalSettingData } = useGetGeneralSettingQuery({ shop: config.shop });

  const itemStrings = useMemo(() => {
    return ['All', 'Closed orders'];
  }, []);

  const tabs = useMemo(
    () =>
      itemStrings.map((label, index) => ({
        id: `${index}`,
        content: label,
      })),
    [itemStrings],
  );

  const { selectedResources, handleSelectionChange } = useIndexResourceState(orderTableSettings);

  const enhancedHandleSelectionChange = (
    selectionType: SelectionType,
    isSelecting: boolean,
    selection?: string | Range,
    _position?: number,
  ) => {
    handleSelectionChange(selectionType, isSelecting, selection, _position);
    document.body.click();
  };

  const handleDueDateChange = useCallback(
    (value: ORDER_DUE_DATE_TYPE[]) => {
      const wasCustomSelected = orderFilterSettings.dueDate.includes(ORDER_DUE_DATE_TYPE.CUSTOM_DATE);
      const isCustomSelectedNow = value.includes(ORDER_DUE_DATE_TYPE.CUSTOM_DATE);

      dispatch(
        handleSaveOrderFilterTableSettings({
          dueDate: value,
        }),
      );

      if (!wasCustomSelected && isCustomSelectedNow) {
        setShowModal((prev) => ({ ...prev, isShowCalendarModal: true }));
      }
    },
    [dispatch, orderFilterSettings.dueDate],
  );

  const handleFulfillmentChange = useCallback(
    (value: ORDER_FULFILLMENT_TYPE[]) => {
      dispatch(
        handleSaveOrderFilterTableSettings({
          fulfillment: value,
        }),
      );
    },
    [dispatch],
  );

  const handleLocationChange = useCallback(
    (value: string[]) => {
      dispatch(
        handleSaveOrderFilterTableSettings({
          location: value,
        }),
      );
    },
    [dispatch],
  );

  const handleOrderStatusChange = useCallback(
    (value: string[]) => {
      dispatch(
        handleSaveOrderFilterTableSettings({
          orderStatus: value,
        }),
      );
    },
    [dispatch],
  );

  const handleClearAll = useCallback(() => {
    if (
      !isEmpty(orderFilterSettings.fulfillment) ||
      !isEmpty(orderFilterSettings.dueDate) ||
      !isEmpty(orderFilterSettings.location) ||
      !isEmpty(orderFilterSettings.orderStatus)
    ) {
      dispatch(
        handleSaveOrderFilterTableSettings({
          dueDate: [],
          fulfillment: [],
          location: [],
          orderStatus: [],
        }),
      );
    }
  }, [
    dispatch,
    orderFilterSettings.dueDate,
    orderFilterSettings.fulfillment,
    orderFilterSettings.location,
    orderFilterSettings.orderStatus,
  ]);

  const handleCancel = useCallback(() => {
    handleClearAll();
    setSearch((prev) => ({ ...prev, queryValue: '' }));
  }, [handleClearAll]);

  const debounceQuery = useRef(
    debounce((value) => {
      setSearch((prev) => ({ ...prev, debouncedSearchValue: value }));
    }, 300),
  ).current;

  useEffect(() => {
    debounceQuery(search.queryValue);
    return () => debounceQuery.cancel();
  }, [debounceQuery, search.queryValue]);

  const dueDateFilterOptions = useMemo(
    () => [
      { label: t('reportTable.dueDateFilter.today'), value: ORDER_DUE_DATE_TYPE.TODAY },
      { label: t('reportTable.dueDateFilter.tomorrow'), value: ORDER_DUE_DATE_TYPE.TOMORROW },
      { label: t('reportTable.dueDateFilter.noDate'), value: ORDER_DUE_DATE_TYPE.NO_DATE },
      { label: t('reportTable.dueDateFilter.custom'), value: ORDER_DUE_DATE_TYPE.CUSTOM_DATE },
    ],
    [t],
  );

  const fulfillmentOptions = useMemo(
    () => [
      { label: t('reportTable.fulfillmentFilter.pickup'), value: ORDER_FULFILLMENT_TYPE.PICKUP },
      { label: t('reportTable.fulfillmentFilter.delivery'), value: ORDER_FULFILLMENT_TYPE.DELIVERY },
      { label: t('reportTable.fulfillmentFilter.shipping'), value: ORDER_FULFILLMENT_TYPE.SHIPPING },
    ],
    [t],
  );

  const listLocationOptions: IOrderLocation[] = useMemo(() => {
    return (
      orderLocationData?.data.map((item) => ({
        value: item.id.toString(),
        label: item.name,
      })) || []
    );
  }, [orderLocationData?.data]);

  const orderStatusOptions = useMemo(
    () => [
      { label: t('reportTable.orderStatusFilter.unfulfilled'), value: ORDER_STATUS_TYPE.UNFULFILLED },
      { label: t('reportTable.orderStatusFilter.fulfilled'), value: ORDER_STATUS_TYPE.FULFILLED },
      { label: t('reportTable.orderStatusFilter.pickup'), value: ORDER_STATUS_TYPE.PICKUP },
      { label: t('reportTable.orderStatusFilter.delivery'), value: ORDER_STATUS_TYPE.DELIVERY },
      { label: t('reportTable.orderStatusFilter.dispatch'), value: ORDER_STATUS_TYPE.DISPATCHED },
    ],
    [t],
  );

  const sortOrder = useMemo(() => {
    if (!sortSelected || sortSelected.length === 0 || !sortSelected[0]) {
      return undefined;
    }
    return {
      sortBy: sortSelected[0]?.split(' ')[0],
      sortType: sortSelected[0]?.split(' ')[1].toUpperCase(),
    };
  }, [sortSelected]);

  const filters = [
    {
      key: 'dueDate',
      label: t('reportTable.dueDateFilter.title'),
      filter: (
        <ChoiceList
          title={t('reportTable.dueDateFilter.title')}
          titleHidden
          choices={dueDateFilterOptions}
          selected={orderFilterSettings.dueDate}
          onChange={handleDueDateChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'fulfillment',
      label: t('reportTable.fulfillmentFilter.title'),
      filter: (
        <ChoiceList
          title={t('reportTable.fulfillmentFilter.title')}
          titleHidden
          choices={fulfillmentOptions}
          selected={orderFilterSettings.fulfillment}
          onChange={handleFulfillmentChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'location',
      label: t('reportTable.location.title'),
      filter: (
        <ChoiceList
          title={t('reportTable.location.title')}
          titleHidden
          choices={listLocationOptions}
          selected={orderFilterSettings.location}
          onChange={handleLocationChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'orderStatus',
      label: t('reportTable.orderStatusFilter.title'),
      filter: (
        <ChoiceList
          title={t('reportTable.orderStatusFilter.title')}
          titleHidden
          choices={orderStatusOptions}
          selected={orderFilterSettings.orderStatus}
          onChange={handleOrderStatusChange}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = useMemo(() => {
    const filters = [];

    if (orderFilterSettings.dueDate.length > 0) {
      let dueDateLabel = orderFilterSettings.dueDate
        .map((value) => dueDateFilterOptions.find((option) => option.value === value)?.label)
        .filter(Boolean)
        .join(', ');

      if (orderFilterSettings.dueDate.includes(ORDER_DUE_DATE_TYPE.CUSTOM_DATE)) {
        if (dateRange.start && dateRange.end) {
          let customDateLabel = `${
            dueDateFilterOptions.find((option) => option.value === ORDER_DUE_DATE_TYPE.CUSTOM_DATE)?.label
          }: (${new Date(dateRange.start).toLocaleDateString()} - ${new Date(dateRange.end).toLocaleDateString()})`;
          let otherDateLabel = orderFilterSettings.dueDate
            .filter((value) => value !== ORDER_DUE_DATE_TYPE.CUSTOM_DATE)
            .map((value) => dueDateFilterOptions.find((option) => option.value === value)?.label)
            .filter(Boolean)
            .join(', ');

          dueDateLabel = `${otherDateLabel.length > 0 ? `${otherDateLabel}, ` : ''} ${customDateLabel}`;
        }
      }

      filters.push({
        key: 'dueDate',
        label: dueDateLabel,
        onRemove: () => {
          dispatch(handleSaveOrderFilterTableSettings({ dueDate: [], customDate: { start: '', end: '' } }));
          setDateRange({ start: '', end: '' });
        },
      });
    }

    if (orderFilterSettings.fulfillment.length > 0) {
      filters.push({
        key: 'fulfillment',
        label: `${orderFilterSettings.fulfillment
          .map((value) => fulfillmentOptions.find((option) => option.value === value)?.label)
          .filter(Boolean)
          .join(', ')}`,
        onRemove: () => dispatch(handleSaveOrderFilterTableSettings({ fulfillment: [] })),
      });
    }

    if (orderFilterSettings.location.length > 0) {
      filters.push({
        key: 'location',
        label: `${orderFilterSettings.location
          .map((value) => listLocationOptions.find((option) => option.value === value)?.label)
          .filter(Boolean)
          .join(', ')}`,
        onRemove: () => dispatch(handleSaveOrderFilterTableSettings({ location: [] })),
      });
    }

    if (orderFilterSettings.orderStatus.length > 0) {
      filters.push({
        key: 'orderStatus',
        label: `${orderFilterSettings.orderStatus
          .map((value) => orderStatusOptions.find((option) => option.value === value)?.label)
          .filter(Boolean)
          .join(', ')}`,
        onRemove: () => dispatch(handleSaveOrderFilterTableSettings({ orderStatus: [] })),
      });
    }

    return filters;
  }, [
    orderFilterSettings.dueDate,
    orderFilterSettings.fulfillment,
    orderFilterSettings.location,
    orderFilterSettings.orderStatus,
    dueDateFilterOptions,
    dateRange.start,
    dateRange.end,
    dispatch,
    fulfillmentOptions,
    listLocationOptions,
    orderStatusOptions,
  ]);

  const dueDateFilters = useMemo(() => {
    const dueDateFilters = [];

    if (orderFilterSettings.dueDate.includes(ORDER_DUE_DATE_TYPE.TODAY)) {
      dueDateFilters.push({
        startDate: today.toISOString().split('T')[0] + 'T00:00:00.000Z',
        endDate: today.toISOString().split('T')[0] + 'T23:59:59.999Z',
      });
    }

    if (orderFilterSettings.dueDate.includes(ORDER_DUE_DATE_TYPE.TOMORROW)) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);

      dueDateFilters.push({
        startDate: tomorrow.toISOString().split('T')[0] + 'T00:00:00.000Z',
        endDate: tomorrow.toISOString().split('T')[0] + 'T23:59:59.999Z',
      });
    }

    if (orderFilterSettings.dueDate.includes(ORDER_DUE_DATE_TYPE.NO_DATE)) {
      dueDateFilters.push({
        startDate: null,
        endDate: null,
      });
    }

    if (orderFilterSettings.dueDate.includes(ORDER_DUE_DATE_TYPE.CUSTOM_DATE)) {
      if (dateRange.start && dateRange.end) {
        dueDateFilters.push({
          startDate: formatDate(new Date(dateRange.start)).split('T')[0] + 'T00:00:00.000Z',
          endDate: formatDate(new Date(dateRange.end)).split('T')[0] + 'T23:59:59.999Z',
        });
      }
    }

    return dueDateFilters;
  }, [dateRange.end, dateRange.start, orderFilterSettings.dueDate, today]);

  const sortOptions: IndexFiltersProps['sortOptions'] = useMemo(
    () => [
      { label: 'Order', value: 'orderNumber asc', directionLabel: 'Ascending' },
      { label: 'Order', value: 'orderNumber desc', directionLabel: 'Descending' },
      { label: 'Created date', value: 'createdAt asc', directionLabel: 'Oldest to newest' },
      { label: 'Created date', value: 'createdAt desc', directionLabel: 'Newest to oldest' },
      { label: 'Total', value: 'totalPrice asc', directionLabel: 'Lowest to highest' },
      { label: 'Total', value: 'totalPrice desc', directionLabel: 'Highest to lowest' },
      { label: 'Fulfillment status ', value: 'fulfillmentStatus asc', directionLabel: 'A-Z' },
      { label: 'Fulfillment status ', value: 'fulfillmentStatus desc', directionLabel: 'Z-A' },
    ],
    [],
  );

  const handleCancelCalendar = () => {
    setShowModal((prev) => ({ ...prev, isShowCalendarModal: false }));
    dispatch(
      handleSaveOrderFilterTableSettings({
        dueDate: orderFilterSettings.dueDate.filter((item) => item !== ORDER_DUE_DATE_TYPE.CUSTOM_DATE),
      }),
    );
  };

  const handleExportOrders = () => {
    exportOrderReportCSV({
      shop: config.shop,
      type: ORDER_REPORT_CSV_TYPE.SPECIFIC,
      orderIds: selectedResources.map((item) => Number(item)),
    })
      .unwrap()
      .then((res) => {
        const url = window.URL.createObjectURL(res.file);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'orders_report.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      });
  };

  const handleExportProductSummary = () => {
    exportProductSummary({
      shop: config.shop,
      orderIds: selectedResources.map((item) => Number(item)),
    })
      .unwrap()
      .then((res) => {
        const url = window.URL.createObjectURL(res.file);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'orders_report_product_summary.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      });
  };

  const handleDateRange = () => {
    setShowModal((prev) => ({ ...prev, isShowCalendarModal: false }));
    setDateRange({
      start: orderFilterSettings.customDate?.start || '',
      end: orderFilterSettings.customDate?.end || '',
    });
  };

  const handleUpdateStatus = (orderStatus: ORDER_STATUS_TYPE) => {
    updateOrderStatus({
      shop: config.shop,
      orderIds: selectedResources.map((item) => Number(item)),
      status: orderStatus,
    }).then(() => setIsCallbackApi(!isCallbackApi));
  };

  const bulkActions = [
    {
      title: t('reportTable.bulkAction.updateStatus.title'),
      actions: [
        {
          content: t('reportTable.bulkAction.updateStatus.pickup'),
          onAction: () => handleUpdateStatus(ORDER_STATUS_TYPE.PICKUP),
        },
        {
          content: t('reportTable.bulkAction.updateStatus.delivery'),
          onAction: () => handleUpdateStatus(ORDER_STATUS_TYPE.DELIVERY),
        },
        {
          content: t('reportTable.bulkAction.updateStatus.dispatch'),
          onAction: () => handleUpdateStatus(ORDER_STATUS_TYPE.DISPATCHED),
        },
        {
          content: t('reportTable.bulkAction.updateStatus.fulfilled'),
          onAction: () => handleUpdateStatus(ORDER_STATUS_TYPE.FULFILLED),
        },
      ],
    },
    {
      content: t('reportTable.bulkAction.closeOrder'),
      onAction: () => handleUpdateStatus(ORDER_STATUS_TYPE.CLOSED),
    },
    {
      title: t('reportTable.bulkAction.export.title'),
      actions: [
        { content: t('reportTable.bulkAction.export.product'), onAction: handleExportProductSummary },
        { content: t('reportTable.bulkAction.export.order'), onAction: handleExportOrders },
      ],
    },
  ];

  useEffect(() => {
    isFilterApplied.current = true;
    dispatch(handleSaveOrderPaginationSettings({ page: 1 }));
  }, [
    dispatch,
    orderFilterSettings.fulfillment,
    orderFilterSettings.location,
    search.debouncedSearchValue,
    orderFilterSettings.tab,
  ]);

  useEffect(() => {
    if (isFilterApplied.current && orderPaginationSettings.page !== 1) {
      return;
    }
    getReportSummary({
      orderType: Number(orderFilterSettings.tab),
      shop: config.shop,
      offset: (orderPaginationSettings.page - 1) * orderPaginationSettings.perPage,
      limit: orderPaginationSettings.perPage,
      search: search.debouncedSearchValue,
      filter: {
        fulfillment: orderFilterSettings.fulfillment.map((item) => Number(item)),
        status: orderFilterSettings.orderStatus[0],
        location: orderFilterSettings.location.map((item) => Number(item)),
        dueDate: dueDateFilters,
      },
      sort: sortOrder,
    })
      .unwrap()
      .then((res) => {
        dispatch(handleSaveOrderTableSettings(res.data.orders));
        dispatch(
          handleSaveOrderPaginationSettings({
            hasNext: res.data.hasNextPage,
            hasPreviouse: res.data.hasPreviousPage,
            totalRecord: res.data.totalRecord,
          }),
        );
      })
      .catch(() => {
        dispatch(handleSaveOrderTableSettings([]));
        dispatch(
          handleSaveOrderPaginationSettings({
            hasNext: false,
            hasPreviouse: false,
            totalRecord: 0,
          }),
        );
      });
    isFilterApplied.current = false;
  }, [
    dispatch,
    dueDateFilters,
    getReportSummary,
    orderFilterSettings.fulfillment,
    orderFilterSettings.location,
    orderFilterSettings.orderStatus,
    orderPaginationSettings.page,
    orderPaginationSettings.perPage,
    search.debouncedSearchValue,
    orderFilterSettings.tab,
    sortOrder,
    isCallbackApi,
  ]);

  const orderColumns = useMemo(
    () => generalSettingData?.data.orderColumn || DEFAULT_ORDER_COLUMN,
    [generalSettingData?.data.orderColumn],
  );

  const showColumns = useMemo(() => orderColumns.filter((item) => item.visible).map((item) => item.key), [orderColumns]);

  const labelToKeyMap: { [key: string]: string } = useMemo(() => {
    return {
      Order: 'order',
      'Created date': 'createdAt',
      Total: 'total',
      'Fulfillment status': 'status',
      Payment: 'payment',
      'Due date': 'dueDate',
      Method: 'method',
      'Ship to': 'shipTo',
      Location: 'location',
      Customer: 'customer',
      Item: 'item',
    };
  }, []);

  const sortOptionsFilter = useMemo(
    () =>
      sortOptions.filter((item) => {
        const correspondingKey = labelToKeyMap[item.label.trim()];
        return showColumns.includes(correspondingKey);
      }),
    [sortOptions, labelToKeyMap, showColumns],
  );

  useEffect(() => {
    if (sortSelected && !sortOptionsFilter.some((option) => option.value === sortSelected[0]) && sortOptionsFilter.length !== 0) {
      setSortSelected([sortOptionsFilter[0]?.value]);
    }
  }, [sortOptionsFilter, sortSelected]);

  const headingsIndexTable = useMemo(
    () => [
      {
        title: t('reportTable.column.order'),
        id: 'order',
      },
      {
        title: t('reportTable.column.created'),
        id: 'createdAt',
      },
      {
        title: t('reportTable.column.item'),
        id: 'item',
      },
      {
        title: t('reportTable.column.total'),
        id: 'total',
      },
      { title: t('reportTable.column.pay'), id: 'payment' },
      {
        title: t('reportTable.column.date'),
        id: 'dueDate',
      },
      { title: t('reportTable.column.method'), id: 'method' },
      { title: t('reportTable.column.location'), id: 'location' },
      { title: t('reportTable.column.ship'), id: 'shipTo' },
      {
        id: 'status',
        title: (
          <HeadingInformation
            title={t('reportTable.column.status')}
            tooltipContent={t('reportTable.column.statusTooltip')}
            minWidth="65px"
          />
        ),
      },
      {
        id: 'customer',
        title: <CenterHeading>{t('reportTable.column.customer')}</CenterHeading>,
      },
    ],
    [t],
  );

  const filteredHeadings = useMemo(
    () =>
      headingsIndexTable
        .filter((column) => showColumns.includes(column.id))
        .sort((a, b) => showColumns.indexOf(a.id) - showColumns.indexOf(b.id)),
    [headingsIndexTable, showColumns],
  );

  return (
    <Card padding={'0'}>
      <IndexFilterStyled>
        <IndexFilters
          sortOptions={sortOptionsFilter}
          sortSelected={sortSelected}
          onSort={setSortSelected}
          mode={mode}
          setMode={setMode}
          queryValue={search.queryValue}
          queryPlaceholder="Search by order number"
          onQueryChange={(value) => setSearch((prev) => ({ ...prev, queryValue: value }))}
          onQueryClear={() => setSearch((prev) => ({ ...prev, queryValue: '', debouncedSearchValue: '' }))}
          onClearAll={handleClearAll}
          cancelAction={{
            onAction: handleCancel,
            disabled: false,
            loading: false,
          }}
          tabs={tabs}
          selected={Number(orderFilterSettings.tab) - 1}
          onSelect={(selectedTabIndex) => dispatch(handleSaveOrderFilterTableSettings({ tab: selectedTabIndex + 1 }))}
          filters={filters}
          appliedFilters={appliedFilters}
          showEditColumnsButton
          onEditStart={(mode) => {
            if (mode === IndexFiltersMode.EditingColumns) {
              setMode(IndexFiltersMode.Default);
              setShowModal((prev) => ({ ...prev, isShowEditColumnModal: true }));
            }
          }}
        />
      </IndexFilterStyled>
      <Box padding={'0'}>
        <BlockStack gap="200">
          <CustomPaddingBox>
            <IndexTable
              key={`${orderFilterSettings.tab}-${showColumns.map((item) => item)}`}
              loading={isLoadingGetOrderReport}
              resourceName={resourceName}
              itemCount={orderTableSettings.length}
              selectedItemsCount={selectedResources.length}
              onSelectionChange={enhancedHandleSelectionChange}
              promotedBulkActions={bulkActions}
              bulkActions={useBreakpoints().mdDown ? bulkActions : []}
              emptyState={
                <EmptySearchResult
                  title={t('reportTable.emptyState.title')}
                  description={t('reportTable.emptyState.desc')}
                  withIllustration
                />
              }
              headings={filteredHeadings as NonEmptyArray<IndexTableHeading>}
              selectable={true}
            >
              {orderTableSettings.map((item, index) => (
                <IndexTable.Row id={item.id} key={item.id} position={index} selected={selectedResources.includes(item.id)}>
                  <RowData dataSource={item} index={index} key={`${index}-${item.id}`} showColumns={showColumns} />
                </IndexTable.Row>
              ))}
            </IndexTable>
          </CustomPaddingBox>
        </BlockStack>
        <Box padding={'300'}>
          <InlineStack align="space-between" blockAlign="center">
            <RegularText>{t('reportTable.totalOrders', { total: orderPaginationSettings.perPage })}</RegularText>
            <Pagination
              hasNext={orderPaginationSettings.hasNext}
              onNext={() => {
                dispatch(handleSaveOrderPaginationSettings({ page: orderPaginationSettings.page + 1 }));
              }}
              hasPrevious={orderPaginationSettings.hasPreviouse}
              onPrevious={() => {
                dispatch(handleSaveOrderPaginationSettings({ page: orderPaginationSettings.page - 1 }));
              }}
              label={`${orderPaginationSettings.page} / ${
                Math.ceil(orderPaginationSettings.totalRecord / orderPaginationSettings.perPage) || '1'
              }`}
            />
          </InlineStack>
        </Box>
      </Box>
      <CalendarModal open={showModal.isShowCalendarModal} onSubmit={handleDateRange} onCancel={handleCancelCalendar} />
      <EditColumnModal
        open={showModal.isShowEditColumnModal}
        showColumns={showColumns}
        orderColumns={orderColumns}
        onSubmit={() => {}}
        onCancel={() => {
          setShowModal((prev) => ({ ...prev, isShowEditColumnModal: false }));
        }}
      />
    </Card>
  );
};

export default OrderTable;
